import React, { useState, useRef, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';

interface MessageInputProps {
  onSendMessage: (message: string) => void;
  disabled: boolean;
  onNewSession: () => void;
}

const MessageInput: React.FC<MessageInputProps> = ({ onSendMessage, disabled, onNewSession }) => {
  const { theme, setTheme } = useTheme();
  const [message, setMessage] = useState('');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleSubmit = () => {
    const trimmedMessage = message.trim();
    if (!trimmedMessage || disabled) return;
    
    onSendMessage(trimmedMessage);
    setMessage('');
    
    // Reset textarea height to natural single-line height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      // Let it size naturally to its content (empty now), respecting min-height
    }
  };

  const themeOptions = [
    { value: 'light' as const, label: 'Light' },
    { value: 'dark' as const, label: 'Dark' },
    { value: 'system' as const, label: 'System' }
  ];

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="p-4">
      <div className="max-w-4xl mx-auto">
        {/* Floating Input Container */}
        <div className="relative bg-white dark:bg-exc-true-gray rounded-lg border border-gray-300 dark:border-gray-600 
                        shadow-lg focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-transparent">
          <div className="flex items-end gap-3 p-3">
            {/* Menu Button - Inside Input */}
            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors mb-1"
                type="button"
                title="Settings"
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd"/>
                </svg>
              </button>

              {/* Context Menu */}
              {isMenuOpen && (
                <div className="absolute bottom-full mb-2 left-0 w-48 bg-white dark:bg-gray-800 
                               rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-2 z-50">
                  
                  {/* Theme Options */}
                  <div className="px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Theme
                  </div>
                  {themeOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => {
                        setTheme(option.value);
                        setIsMenuOpen(false);
                      }}
                      className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 
                                 flex items-center gap-3 ${
                                   theme === option.value 
                                     ? 'bg-gray-100 dark:bg-gray-700 text-primary-600 dark:text-primary-400' 
                                     : 'text-gray-700 dark:text-gray-300'
                                 }`}
                    >
                      <span>{option.label}</span>
                      {theme === option.value && (
                        <span className="ml-auto">
                          <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                          </svg>
                        </span>
                      )}
                    </button>
                  ))}

                  <hr className="my-2 border-gray-200 dark:border-gray-700" />

                  {/* New Session */}
                  <button
                    onClick={() => {
                      onNewSession();
                      setIsMenuOpen(false);
                    }}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 
                               text-gray-700 dark:text-gray-300"
                  >
                    New Session
                  </button>
                </div>
              )}
            </div>

            {/* Textarea Input */}
            <div className="flex-1 relative">
              <textarea
                ref={textareaRef}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmit();
                  }
                }}
                placeholder="Send a message..."
                rows={1}
                className="w-full resize-none pr-10 py-2 bg-transparent text-gray-900 dark:text-gray-100 
                           placeholder-gray-500 dark:placeholder-gray-400 border-none focus:outline-none
                           transition-all duration-200"
                style={{
                  minHeight: '40px',
                  maxHeight: '120px',
                }}
                disabled={disabled}
                onInput={(e) => {
                  const target = e.target as HTMLTextAreaElement;
                  target.style.height = 'auto';
                  target.style.height = Math.min(target.scrollHeight, 120) + 'px';
                }}
              />

              {/* Send Button */}
              <button 
                onClick={handleSubmit}
                disabled={disabled || !message.trim()}
                className="absolute right-2 bottom-2 
                           p-1.5 bg-gray-100 hover:bg-gray-200 disabled:bg-gray-100
                           dark:bg-gray-700 dark:hover:bg-gray-600 dark:disabled:bg-gray-700
                           text-gray-600 disabled:text-gray-400 dark:text-gray-300 dark:disabled:text-gray-500
                           rounded-full transition-colors disabled:cursor-not-allowed"
              >
                <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessageInput;